/**
 * ClassificationCell Component
 *
 * Inline classification cell for the transactions table.
 * Supports display mode (showing current classification) and edit mode
 * (AccountTreeSelect with owner toggle).
 *
 * @see types/accounting.ts for Transaction and Account types
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import clsx from 'clsx';
import { Pencil, Loader2, X, Check, AlertCircle } from 'lucide-react';
import type { Transaction, Account, Owner } from '@/types/accounting';
import { AccountTreeSelect } from '../../shared/AccountTreeSelect';

export interface ClassificationCellProps {
  /** The transaction being classified */
  transaction: Transaction;
  /** Flat list of all accounts */
  accounts: Account[];
  /** Hierarchical tree of accounts */
  accountTree: Account[];
  /** Callback when classification is saved */
  onClassify: (accountId: number, owner: Owner) => Promise<void>;
  /** Whether the cell is currently being edited */
  isEditing?: boolean;
  /** Callback when edit mode is toggled */
  onEditToggle?: (isEditing: boolean) => void;
}

/**
 * ClassificationCell renders an inline classification editor.
 *
 * States:
 * 1. Display mode: Shows current category or "Unclassified" with edit button
 * 2. Edit mode: Shows AccountTreeSelect + owner toggle + save/cancel
 * 3. Loading mode: Shows spinner during save
 * 4. Error mode: Shows error message with retry option
 *
 * Features:
 * - Inline editing without modal
 * - Account type filtering (expense/income only)
 * - Owner toggle (family/estate)
 * - Keyboard navigation (Enter to edit, Escape to cancel)
 * - Loading and error states
 *
 * @example
 * ```tsx
 * <ClassificationCell
 *   transaction={transaction}
 *   accounts={accounts}
 *   accountTree={accountTree}
 *   onClassify={async (accountId, owner) => {
 *     await classifyTransaction(transaction.id, accountId, owner);
 *   }}
 * />
 * ```
 */
export function ClassificationCell({
  transaction,
  accounts,
  accountTree,
  onClassify,
  isEditing: externalIsEditing,
  onEditToggle,
}: ClassificationCellProps) {
  // Use external editing state if provided, otherwise manage internally
  const [internalIsEditing, setInternalIsEditing] = useState(false);
  const isEditing = externalIsEditing !== undefined ? externalIsEditing : internalIsEditing;

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedAccountId, setSelectedAccountId] = useState<number | null>(
    transaction.expenseAccount?.id ?? null
  );
  const [owner, setOwner] = useState<Owner>(transaction.owner);

  const containerRef = useRef<HTMLDivElement>(null);

  // Reset state when transaction changes
  useEffect(() => {
    setSelectedAccountId(transaction.expenseAccount?.id ?? null);
    setOwner(transaction.owner);
    setError(null);
  }, [transaction.id, transaction.expenseAccount?.id, transaction.owner]);

  // Handle entering edit mode
  const handleStartEdit = useCallback(() => {
    if (onEditToggle) {
      onEditToggle(true);
    } else {
      setInternalIsEditing(true);
    }
    setError(null);
  }, [onEditToggle]);

  // Handle canceling edit
  const handleCancel = useCallback(() => {
    if (onEditToggle) {
      onEditToggle(false);
    } else {
      setInternalIsEditing(false);
    }
    // Reset to original values
    setSelectedAccountId(transaction.expenseAccount?.id ?? null);
    setOwner(transaction.owner);
    setError(null);
  }, [onEditToggle, transaction.expenseAccount?.id, transaction.owner]);

  // Handle saving classification
  const handleSave = useCallback(async () => {
    if (!selectedAccountId) {
      setError('Please select an account');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      await onClassify(selectedAccountId, owner);
      if (onEditToggle) {
        onEditToggle(false);
      } else {
        setInternalIsEditing(false);
      }
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : 'Failed to classify. Please try again.'
      );
    } finally {
      setIsLoading(false);
    }
  }, [selectedAccountId, owner, onClassify, onEditToggle]);

  // Handle keyboard shortcuts
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Escape' && isEditing) {
        e.preventDefault();
        e.stopPropagation();
        handleCancel();
      } else if (e.key === 'Enter' && !isEditing) {
        e.preventDefault();
        handleStartEdit();
      } else if (e.key === 'Enter' && isEditing && e.ctrlKey) {
        e.preventDefault();
        handleSave();
      }
    },
    [isEditing, handleCancel, handleStartEdit, handleSave]
  );

  // Display mode
  if (!isEditing) {
    const categoryDisplay = transaction.expenseAccount
      ? transaction.expenseAccount.fullPath
      : 'Unclassified';
    const isUnclassified = !transaction.expenseAccount;

    return (
      <div
        ref={containerRef}
        className="flex items-center justify-between group"
        onKeyDown={handleKeyDown}
        tabIndex={0}
        role="button"
        aria-label={`Classification: ${categoryDisplay}. Press Enter to edit.`}
        data-testid="classification-cell"
      >
        <span
          className={clsx(
            'truncate flex-1',
            isUnclassified && 'text-gray-400 italic'
          )}
          title={categoryDisplay}
        >
          {transaction.expenseAccount?.name || 'Unclassified'}
        </span>
        <button
          type="button"
          onClick={handleStartEdit}
          className={clsx(
            'opacity-0 group-hover:opacity-100',
            'p-1 rounded',
            'hover:bg-gray-100',
            'focus:opacity-100 focus:outline-none focus:ring-2 focus:ring-blue-500',
            'transition-opacity duration-150'
          )}
          aria-label="Edit classification"
          data-testid="edit-classification-button"
        >
          <Pencil className="w-4 h-4 text-gray-400" aria-hidden="true" />
        </button>
      </div>
    );
  }

  // Edit mode
  return (
    <div
      ref={containerRef}
      className="space-y-2 py-1"
      onKeyDown={handleKeyDown}
      data-testid="classification-cell-edit"
    >
      {/* Account picker */}
      <AccountTreeSelect
        accounts={accounts}
        accountTree={accountTree}
        value={selectedAccountId}
        onChange={setSelectedAccountId}
        filterTypes={['expense', 'income']}
        placeholder="Select category..."
        disabled={isLoading}
        error={error && !selectedAccountId ? 'Required' : undefined}
        className="w-full"
      />

      {/* Owner toggle */}
      <div className="flex items-center gap-4" role="radiogroup" aria-label="Owner">
        <label className="inline-flex items-center gap-1.5 cursor-pointer">
          <input
            type="radio"
            name={`owner-${transaction.id}`}
            checked={owner === 'family'}
            onChange={() => setOwner('family')}
            disabled={isLoading}
            className="h-4 w-4 border-gray-300 text-blue-600 focus:ring-blue-500"
          />
          <span className="text-sm text-gray-700">Family</span>
        </label>
        <label className="inline-flex items-center gap-1.5 cursor-pointer">
          <input
            type="radio"
            name={`owner-${transaction.id}`}
            checked={owner === 'estate'}
            onChange={() => setOwner('estate')}
            disabled={isLoading}
            className="h-4 w-4 border-gray-300 text-blue-600 focus:ring-blue-500"
          />
          <span className="text-sm text-gray-700">Estate</span>
        </label>
      </div>

      {/* Error message */}
      {error && (
        <div
          className="flex items-center gap-1 text-sm text-red-600"
          role="alert"
          data-testid="classification-error"
        >
          <AlertCircle className="w-4 h-4 flex-shrink-0" aria-hidden="true" />
          <span>{error}</span>
        </div>
      )}

      {/* Action buttons */}
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={handleCancel}
          disabled={isLoading}
          className={clsx(
            'flex items-center gap-1 px-2 py-1 text-sm',
            'rounded',
            isLoading
              ? 'text-gray-300 cursor-not-allowed'
              : 'text-gray-600 hover:bg-gray-100',
            'focus:outline-none focus:ring-2 focus:ring-blue-500'
          )}
          data-testid="cancel-classification"
        >
          <X className="w-4 h-4" aria-hidden="true" />
          Cancel
        </button>
        <button
          type="button"
          onClick={handleSave}
          disabled={isLoading || !selectedAccountId}
          className={clsx(
            'flex items-center gap-1 px-2 py-1 text-sm',
            'rounded',
            isLoading || !selectedAccountId
              ? 'bg-blue-300 text-white cursor-not-allowed'
              : 'bg-blue-500 text-white hover:bg-blue-600',
            'focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2'
          )}
          data-testid="save-classification"
        >
          {isLoading ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />
              Saving...
            </>
          ) : (
            <>
              <Check className="w-4 h-4" aria-hidden="true" />
              Save
            </>
          )}
        </button>
      </div>
    </div>
  );
}

export default ClassificationCell;
